/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useState, useRef, useEffect } from "react";
import radioButtonActive from "@src/assets/svg/radio-button-active.svg";
import radioButtonInactive from "@src/assets/svg/radio-button-inactive.svg";
import searchIcon from "@src/assets/svg/magnifying-glass-gray.svg";
import caretDownIcon from "@src/assets/svg/caret-down.svg";
import styles from "./language-selector-with-search.module.scss";
import { useClickOutsideListener } from "@src/hooks/use-click-outside-listener";
import { ISO3LanguageCode } from "@src/types/user-management";
import { useI18N } from "@src/language/language-context";

interface LanguageOption {
  code: string;
  name: string;
}

interface LanguageSelectorProps {
  selectedLanguage: ISO3LanguageCode;
  setSelectedLanguage: (languageCode: ISO3LanguageCode) => void;
  showSearch?: boolean;
}

const cssClassName = "language-selector-with-search";

export const LanguageSelectorWithSearch = ({
  selectedLanguage,
  setSelectedLanguage,
  showSearch = false,
}: LanguageSelectorProps) => {
  const i18n = useI18N();

  const texts = {
    languageLabel: i18n("modules.languageSelectorWithSearch.languageLabel"),
    searchPlaceholder: i18n(
      "modules.languageSelectorWithSearch.searchPlaceholder",
    ),
    noResults: i18n("modules.languageSelectorWithSearch.noResults"),
    languages: {
      eng: i18n("modules.languageSelectorWithSearch.languages.englishLabel"),
      deu: i18n("modules.languageSelectorWithSearch.languages.germanLabel"),
      zho: i18n(
        "modules.languageSelectorWithSearch.languages.chineseSimplifiedLabel",
      ),
      ita: i18n("modules.languageSelectorWithSearch.languages.italianLabel"),
      /* spa: i18n("modules.languageSelectorWithSearch.languages.spanishLabel"), */
      por: i18n(
        "modules.languageSelectorWithSearch.languages.portugueseBrazilianLabel",
      ),
      nld: i18n("modules.languageSelectorWithSearch.languages.dutchLabel"),
      tur: i18n(
        "modules.languageSelectorWithSearch.languages.turkishLabel",
      ),
    },
  };

  const languagesOptions: LanguageOption[] = Object.entries(
    texts.languages,
  ).map(([code, name]) => ({ code, name }));

  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useClickOutsideListener(dropdownRef, () => setIsOpen(false), isOpen);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const filteredLanguages = languagesOptions.filter((lang) =>
    lang.name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const handleLanguageSelect = (languageCode: ISO3LanguageCode) => {
    setSelectedLanguage(languageCode);
    setIsOpen(false);
    setSearchQuery("");
  };

  return (
    <div className={styles[cssClassName]}>
      <p className={styles[`${cssClassName}__label`]}>{texts.languageLabel}</p>
      <div className={styles[`${cssClassName}__selector`]} ref={dropdownRef}>
        <div
          className={styles[`${cssClassName}__header`]}
          onClick={() => setIsOpen(!isOpen)}
        >
          <span className={styles[`${cssClassName}__selected-language`]}>
            {languagesOptions.find((l) => l.code === selectedLanguage)?.name ||
              ""}
          </span>
          <img
            className={styles[`${cssClassName}__caret`]}
            src={caretDownIcon}
            alt="Caret Down"
          />
        </div>

        {isOpen && (
          <div className={styles[`${cssClassName}__dropdown`]}>
            <div className={styles[`${cssClassName}__dropdown-inner`]}>
              {showSearch && (
                <div className={styles[`${cssClassName}__search-container`]}>
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder={texts.searchPlaceholder}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className={styles[`${cssClassName}__search-input`]}
                  />
                  <img
                    src={searchIcon}
                    alt="Search Icon"
                    className={styles[`${cssClassName}__search-icon`]}
                  />
                </div>
              )}

              <div className={styles[`${cssClassName}__list`]}>
                {filteredLanguages.length > 0 ? (
                  filteredLanguages.map((language) => (
                    <div
                      key={language.code}
                      className={styles[`${cssClassName}__option`]}
                      onClick={() =>
                        handleLanguageSelect(language.code as ISO3LanguageCode)
                      }
                    >
                      <div className={styles[`${cssClassName}__radio`]}>
                        <img
                          src={
                            selectedLanguage === language.code
                              ? radioButtonActive
                              : radioButtonInactive
                          }
                          alt="Language Radio Button"
                        />
                      </div>
                      <span className={styles[`${cssClassName}__radio-label`]}>
                        {language.name}
                      </span>
                    </div>
                  ))
                ) : (
                  <div className={styles[`${cssClassName}__no-results`]}>
                    {texts.noResults}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
